import { createContext, useContext, useEffect, useState } from 'react';
import type { PortalGateCode } from '../../dtos/portal/portal.dto';
import { portalGateCode } from '../../services/portal/portal.service';

/**
 * Lets a screen tell the shell that the guardian gate closed (pending / ended / unavailable)
 * for the patient it was reading. The shell decides whether that is per-patient or shell-wide.
 */
export const PortalGateContext = createContext<(code: PortalGateCode, patientId?: string) => void>(() => undefined);

export interface PortalResource<T> {
  data: T | null;
  loading: boolean;
  /** Safe message to show (never PHI, never raw server text) */
  error: string | null;
}

/**
 * Load one guardian read for the selected patient. Gate codes are handed to the shell together
 * with the patientId; other failures show a generic message.
 */
export function usePortalResource<T>(fetcher: (patientId: string) => Promise<T>, patientId: string): PortalResource<T> {
  const onGate = useContext(PortalGateContext);
  const [state, setState] = useState<PortalResource<T>>({ data: null, loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    fetcher(patientId)
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const code = portalGateCode(err);
        if (code && code !== 'FORBIDDEN') {
          onGate(code, patientId);
          return;
        }
        setState({
          data: null,
          loading: false,
          error: code === 'FORBIDDEN'
            ? "You don't have access to this information."
            : 'This information could not be loaded. Please try again later.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [fetcher, patientId, onGate]);

  return state;
}
